// components/PatientBotGate.js
// AIden unlocks through ANY of the 4 registration paths — telehealth,
// through-my-clinician, the free old-Proove-results conversion, or any
// completed payment (cash panel, copay, or the pay-per-use other-lab PGx
// upload) — not just the old single upload_interpretation payment. Wraps
// <BotInterface botType="patient" /> at the /patient-bot route: checks
// GET /me/aiden-access on mount (mirrors the backend's _has_aiden_access
// exactly) and shows a locked screen with a link to My Payments instead of
// the chat if none of the 4 paths are satisfied yet. The backend enforces
// this same gate on every patient-bot analyze/ask endpoint (402), so this
// is a UX convenience, not the only enforcement point.
import React, { useContext, useEffect, useState } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link } from "react-router-dom";
import LockIcon from "@mui/icons-material/LockOutlined";
import axios from "axios";
import UserContext from "./UserContext";
import BotInterface from "./BotInterface";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#0a0e27", navyDark: "#0f1233", accent: "#637cdb" };

export default function PatientBotGate() {
  const { user } = useContext(UserContext);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const token = user?.token;

  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [lockedMessage, setLockedMessage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/me/aiden-access`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setAllowed(Boolean(res.data.allowed));
        setLockedMessage(res.data.locked_message || null);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || "Failed to check your account status. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", bgcolor: isDark ? "#0a0f1e" : "#f0f4ff" }}>
        <CircularProgress sx={{ color: BRAND.accent }} />
      </Box>
    );
  }

  if (allowed) {
    return <BotInterface botType="patient" />;
  }

  return (
    <Box sx={{
      display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh",
      bgcolor: isDark ? "#0a0f1e" : "#f0f4ff", p: 3,
    }}>
      <Box sx={{
        maxWidth: 440, width: "100%", borderRadius: "16px", p: 4, textAlign: "center",
        bgcolor: isDark ? "#111827" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)"}`,
      }}>
        <Box sx={{
          width: 56, height: 56, mx: "auto", mb: 2, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: isDark ? "rgba(99,124,219,0.15)" : "rgba(99,124,219,0.1)",
        }}>
          <LockIcon sx={{ color: BRAND.accent, fontSize: 26 }} />
        </Box>
        <Typography sx={{ fontSize: 19, fontWeight: 800, color: isDark ? "#e2e8f0" : "#1e293b", mb: 1 }}>
          Unlock AIden
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: isDark ? "#94a3b8" : "#64748b", mb: 3, lineHeight: 1.6 }}>
          {lockedMessage || "AIden unlocks once you've completed registration through one of our testing options — visit My Payments or finish registration to get access."}
        </Typography>
        {error && (
          <Typography sx={{ fontSize: 12.5, color: "#ef4444", mb: 2 }}>{error}</Typography>
        )}
        <Button
          fullWidth variant="contained" component={Link} to="/my-payments"
          sx={{ bgcolor: BRAND.navy, py: 1.2, fontWeight: 700, "&:hover": { bgcolor: BRAND.navyDark }, mb: 1.5 }}
        >
          Go to My Payments
        </Button>
        <Button component={Link} to="/home" fullWidth variant="text" sx={{ color: isDark ? "#64748b" : "#94a3b8", fontWeight: 600, textTransform: "none", fontSize: 13 }}>
          ← Back to Home
        </Button>
      </Box>
    </Box>
  );
}
